import React, { useState } from 'react';
import { resetData } from '../../services/api';
import { FiAlertTriangle, FiTrash2, FiLock } from 'react-icons/fi';
import Toast from '../../components/Toast';

// v1.9.127 — Reset Data is behind a soft page lock. The admin password + the
// typed DELETE token still gate every actual wipe (both server-side), so this
// lock is a UX guard, not a security boundary. Code is kept in session storage
// so the admin isn't re-prompted on every tab switch inside the same session.
const PAGE_LOCK_CODE = '108120';
const PAGE_LOCK_KEY  = 'reset-data-unlocked';

const CATEGORIES = [
  { key: 'members', label: 'Members, dependents & tezkar', desc: 'All registered members, their dependent family members, and tezkar (memorial) names.' },
  { key: 'status_history', label: 'Member status history', desc: 'Active / inactive / transferred / deceased change log entries.' },
  { key: 'families', label: 'Spiritual families', desc: 'Family records and family-member links.' },
  { key: 'followups', label: 'Follow-ups', desc: 'All follow-up visit records (member and family).' },
  { key: 'subscriptions', label: 'Subscriptions & periods', desc: 'Recurring contribution pledges and their generated billing periods.' },
  { key: 'reminders', label: 'Payment reminders', desc: 'Sent reminder log (admin → member nudges).' },
  { key: 'payment_requests', label: 'Payment requests', desc: 'Member-submitted payments awaiting / past approval.' },
  { key: 'cash_receipts', label: 'Cash Receipts (CR)', desc: 'All recorded cash receipts and their items.' },
  { key: 'payment_vouchers', label: 'Payment Vouchers (PV) & Accounts Payable', desc: 'Outgoing payment authorizations and AP entries.' },
  { key: 'attendance', label: 'Services & attendance', desc: 'Service events and attendance records.' },
  { key: 'member_logins', label: 'Member portal logins', desc: 'All login accounts with role "member" (everyone who registered for the member portal). Your admin and staff logins are NOT touched.' },
];

const ResetData = () => {
  const [selected, setSelected] = useState({});
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  // v1.9.127 — session-scoped page lock. True once the admin has typed the
  // code this browser session; try/catch in case sessionStorage is blocked.
  const [unlocked, setUnlocked] = useState(() => {
    try { return sessionStorage.getItem(PAGE_LOCK_KEY) === '1'; } catch { return false; }
  });
  const [lockCode, setLockCode] = useState('');
  const [lockError, setLockError] = useState('');

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 4000); };

  const tryUnlock = (e) => {
    e?.preventDefault();
    if (lockCode === PAGE_LOCK_CODE) {
      setUnlocked(true);
      setLockError('');
      try { sessionStorage.setItem(PAGE_LOCK_KEY, '1'); } catch {}
    } else {
      setLockError('Wrong code.');
      setLockCode('');
    }
  };

  if (!unlocked) {
    return (
      <div className="page-content">
        <div className="page-header"><div><h1>Reset Data</h1><p>Locked — enter unlock code to continue.</p></div></div>
        <div style={{ maxWidth: 420, margin: '32px auto', padding: 24, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, textAlign: 'center' }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#fee2e2', color: '#991b1b', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
            <FiLock size={24} />
          </div>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>This page is locked</div>
          <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 18, lineHeight: 1.4 }}>
            Reset Data permanently deletes records. Enter the unlock code to open the page.
          </div>
          <form onSubmit={tryUnlock}>
            <input
              type="password"
              inputMode="numeric"
              autoFocus
              value={lockCode}
              onChange={e => { setLockCode(e.target.value); setLockError(''); }}
              placeholder="Unlock code"
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 15, textAlign: 'center', letterSpacing: 4, boxSizing: 'border-box' }}
            />
            {lockError && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 8 }}>{lockError}</div>}
            <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: 14 }}>
              <FiLock /> Unlock
            </button>
          </form>
        </div>
        <Toast message={toast?.msg} type={toast?.type} onClose={() => setToast(null)} />
      </div>
    );
  }

  const allSelected = CATEGORIES.every(c => selected[c.key]);
  const anySelected = CATEGORIES.some(c => selected[c.key]);
  const toggleAll = () => {
    const next = !allSelected;
    setSelected(CATEGORIES.reduce((acc, c) => ({ ...acc, [c.key]: next }), {}));
  };

  const handleWipe = async () => {
    if (!anySelected) return showToast('Pick at least one category', 'error');
    if (!password) return showToast('Enter your admin password', 'error');
    if (confirmText.toUpperCase() !== 'DELETE') return showToast('Type DELETE to confirm', 'error');
    const cats = CATEGORIES.filter(c => selected[c.key]).map(c => c.key);
    if (!window.confirm(`This will permanently delete data from ${cats.length} categor${cats.length === 1 ? 'y' : 'ies'}. This cannot be undone. Continue?`)) return;
    setBusy(true);
    try {
      const r = await resetData({ categories: cats, password });
      const summary = r.data?.summary || {};
      const total = Object.values(summary).reduce((s, n) => s + n, 0);
      const lines = Object.entries(summary).map(([k, v]) => `${k}: ${v}`).join(', ');
      showToast(total > 0 ? `Wiped ${total} row${total === 1 ? '' : 's'} (${lines})` : 'No rows matched (already empty?)');
      setSelected({});
      setPassword('');
      setConfirmText('');
    } catch (e) {
      showToast(e.response?.data?.error || 'Failed to reset data', 'error');
    } finally { setBusy(false); }
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div><h1>Reset Data</h1><p>Wipe selected categories of data and start fresh.</p></div>
      </div>

      <div className="card" style={{ marginBottom: 16, background: '#fee2e2', border: '1px solid #fca5a5', padding: '14px 16px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <FiAlertTriangle style={{ color: '#b91c1c', fontSize: 24, flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontSize: 13, color: '#7f1d1d', lineHeight: 1.6 }}>
          <strong>This is permanent.</strong> Deleted records cannot be recovered without a backup. Your <strong>login</strong>, <strong>church profile / settings</strong>, <strong>fund types</strong>, and <strong>staff users</strong> are NOT touched and remain available.
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>What to delete</span>
          <button className="btn btn-sm btn-secondary" onClick={toggleAll}>
            {allSelected ? 'Clear all' : 'Select all'}
          </button>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {CATEGORIES.map((c, i) => (
            <label key={c.key} style={{ display: 'flex', gap: 12, padding: '12px 20px', borderBottom: i < CATEGORIES.length - 1 ? '1px solid #f3f4f6' : 'none', cursor: 'pointer', alignItems: 'flex-start' }}>
              <input type="checkbox" checked={!!selected[c.key]} onChange={e => setSelected(p => ({ ...p, [c.key]: e.target.checked }))} style={{ marginTop: 4, width: 18, height: 18, cursor: 'pointer' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>{c.label}</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{c.desc}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="card" style={{ padding: 16 }}>
        <div className="form-group">
          <label>Your admin password <span className="required">*</span></label>
          <input type="password" value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Enter password to confirm" autoComplete="current-password" />
        </div>
        <div className="form-group">
          <label>Type <strong style={{ color: '#dc2626' }}>DELETE</strong> to confirm <span className="required">*</span></label>
          <input value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="DELETE" autoComplete="off" />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-danger" onClick={handleWipe}
            disabled={busy || !anySelected || !password || confirmText.toUpperCase() !== 'DELETE'}>
            <FiTrash2 /> {busy ? 'Wiping…' : 'Wipe Selected'}
          </button>
        </div>
      </div>

      <Toast message={toast?.msg} type={toast?.type} onClose={() => setToast(null)} />
    </div>
  );
};

export default ResetData;
